import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { retryClaims } from '@elka/hooks';
import { LoadingSpinner, NotProvisionedScreen } from '@elka/ui';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isLoading, isAuthorized, isUnprovisioned, user, signOut } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Claim OR domain fallback (transition). Authorized users see the app.
  if (isAuthorized) {
    return <>{children}</>;
  }

  // Authenticated but no tenant claim and not on a whitelisted domain: their
  // account exists but isn't assigned to an organization yet.
  if (isUnprovisioned) {
    return (
      <NotProvisionedScreen
        email={user?.email}
        onSignOut={signOut}
        onRetry={user ? async () => void (await retryClaims(user)) : undefined}
      />
    );
  }

  return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
}
